import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import {
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { unwrapResult } from "@reduxjs/toolkit";
import moment from "moment/moment";
import fileSize from "file-size";

import {
  getAttachementById,
  getAttachementList,
} from "../../../../../ReduxTLK/features/Attachement/AttachementSlice";
import { setPaginationModelSAVTA } from "../../../../../ReduxTLK/features/Pagination/PaginationsSlice";
import { Persistor } from "../../../../..";

import DownloadIcon from "../../../../../GlobalComponents/IconComponents/DownloadIcon";
import TrashIcon from "../../../../../GlobalComponents/IconComponents/TrashIcon";
import UploadFile from "../../../../../Pages/SAV/Attachements/UploadFile/UploadFile";
import DeleteConfirmationDialog from "../../../../../Pages/SAV/Attachements/DeleteConfirmationDialog";
import CustomFooterComponent from "../../../../../Pages/SAV/Attachements/CustomFooterComponent";

import styles from "./AttachmentListTab.module.css";

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

const getFileCategory = (mimeType = "") => {
  if (mimeType === "application/pdf") return "pdf";
  const prefix = mimeType.substring(0, mimeType.indexOf("/"));
  if (prefix === "image") return "image";
  if (prefix === "audio") return "audio";
  return "other";
};

const FileTypeIcon = ({ category }) => {
  const map = {
    pdf: { label: "PDF", cls: styles.fileIconPdf },
    image: { label: "IMG", cls: styles.fileIconImage },
    audio: { label: "MP3", cls: styles.fileIconAudio },
    other: { label: "FILE", cls: styles.fileIconDefault },
  };
  const { label, cls } = map[category] || map.other;
  return <span className={`${styles.fileIconWrap} ${cls}`}>{label}</span>;
};

/* ------------------------------------------------------------------ */
/* Empty state                                                        */
/* ------------------------------------------------------------------ */

const EmptyState = () => (
  <div className={styles.emptyState}>
    <div className={styles.emptyIcon}>
      <InsertDriveFileOutlinedIcon sx={{ fontSize: 32 }} />
    </div>
    <div className={styles.emptyTitle}>Aucun attachement</div>
    <div className={styles.emptySubtitle}>
      Aucun fichier n'a encore été ajouté à ce ticket. Utilisez le bouton
      « Importer un fichier » ci-dessus pour en ajouter.
    </div>
  </div>
);

const LoadingState = () => (
  <div className={styles.loadingState}>
    <Spin
      indicator={
        <LoadingOutlined style={{ fontSize: 32, color: "#2A66C6" }} spin />
      }
    />
    <span className={styles.loaderText}>Chargement des attachements...</span>
  </div>
);

/* ------------------------------------------------------------------ */
/* Main Component                                                     */
/* ------------------------------------------------------------------ */

export default function AttachmentListTab(props) {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState();

  const paginationModelSAVTA = useSelector(
    (state) => state.pagination.paginationModelSAVTA
  );

  const [paginationModelCustom, setPaginationModelCustom] = useState({
    pageSize: 7,
    page: 1,
    nbPage: 0,
    idTicket: props.idTicket,
  });

  const [page, setPage] = useState();
  const [openPreview, setOpenPreview] = useState(false);
  const [attachment, setAttachmentToPreview] = useState();
  const [openPreviewImage, setOpenPreviewImage] = useState(false);
  const [openPreviewAudio, setOpenPreviewAudio] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = React.useState();
  const [unseenAttachmentIds, setUnseenAttachmentIds] = useState([]);

  /* ------------------ Preview handlers ------------------ */
  const handlePreview = (row) => {
    const category = getFileCategory(row.type);
    setAttachmentToPreview(row.id);
    if (category === "pdf") setOpenPreview(true);
    else if (category === "image") setOpenPreviewImage(true);
    else if (category === "audio") setOpenPreviewAudio(true);
  };

  /* ------------------ Columns ------------------ */
  const columns = [
    {
      field: "nom",
      headerName: "Nom du fichier",
      flex: 0.35,
      align: "left",
      renderCell: (params) => {
        const category = getFileCategory(params.row.type);
        const cleanName = params?.value?.substring(
          0,
          params?.value?.indexOf(".")
        );
        return (
          <Button
            className={styles.fileButton}
            onClick={() => handlePreview(params.row)}
          >
            <FileTypeIcon category={category} />
            <span className={styles.fileName} title={cleanName}>
              {cleanName}
            </span>
          </Button>
        );
      },
    },
    {
      field: "type",
      headerName: "Type",
      flex: 0.15,
      renderCell: (params) => {
        const ext = params?.value?.substring(
          params?.value?.indexOf("/") + 1,
          params?.value?.length
        );
        return <span className={styles.typePill}>{ext}</span>;
      },
    },
    {
      field: "date",
      headerName: "Date",
      flex: 0.15,
      renderCell: (params) => (
        <span className={styles.cellMuted}>
          {moment(params?.value).format("DD MMM YYYY")}
        </span>
      ),
    },
    {
      field: "taille",
      headerName: "Taille",
      sortable: false,
      flex: 0.12,
      renderCell: (params) => (
        <span className={styles.cellMuted}>
          {fileSize(params?.value).human("jedec")}
        </span>
      ),
    },
    {
      field: "action",
      align: "center",
      headerAlign: "center",
      headerName: "Actions",
      flex: 0.12,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const obj = params.row;
        return (
          <div className={styles.actionGroup}>
            <Tooltip title="Télécharger" arrow placement="top">
              <IconButton
                size="small"
                className={styles.actionButton}
                onClick={() => handleDownload(obj)}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer" arrow placement="top">
              <IconButton
                size="small"
                className={`${styles.actionButton} ${styles.actionButtonDanger}`}
                onClick={() => handleDeleteModal(obj)}
              >
                <TrashIcon />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  /* ------------------ Data fetching ------------------ */
  const fetchData = async (paginationModel) => {
    dispatch(getAttachementList(paginationModel))
      .then(unwrapResult)
      .then((res) => {
        props.setAttachmentList(res?.data?.data?.result);
        setPaginationModelCustom({
          pageSize: res?.data?.data?.pagination?.pageSize,
          page: res?.data?.data?.pagination?.currentPage,
          nbPage: res?.data?.data?.pagination?.nbPage,
          idTicket: props.idTicket,
        });
        dispatch(
          setPaginationModelSAVTA({
            page: res?.data?.pagination?.currentPage,
            pageSize: res?.data?.pagination?.pageSize,
            nbPage: res?.data?.pagination?.nbPage,
            idTicket: props.idTicket,
          })
        );
        Persistor.persist();
      });
  };

  useEffect(() => {
    setIsLoading(true);
    dispatch(getAttachementList(paginationModelCustom)).then((res) => {
      props.setAttachmentList(res?.payload?.data?.data?.result);
      setPaginationModelCustom({
        pageSize: res?.payload?.data?.data?.pagination?.pageSize,
        page: res?.payload?.data?.data?.pagination?.currentPage,
        nbPage: res?.payload?.data?.data?.pagination?.nbPage,
        idTicket: props.idTicket,
      });
      dispatch(
        setPaginationModelSAVTA({
          page: res?.data?.pagination?.currentPage,
          pageSize: res?.data?.pagination?.pageSize,
          nbPage: res?.data?.pagination?.nbPage,
          idTicket: props.idTicket,
        })
      );
      setIsLoading(false);
      Persistor.persist();
    });
  }, []);

  useEffect(() => {
    setIsLoading(true);
    dispatch(getAttachementList(paginationModelCustom)).then((res) => {
      props.setAttachmentList(res?.payload?.data?.data?.result);
      const newUnseen = [];
      for (
        let i = 0;
        i < props.nbreAttachementsNonLus &&
        i < res?.payload?.data?.data?.result?.length;
        i++
      ) {
        newUnseen.push(res?.payload?.data?.data?.result[i].id);
      }
      setUnseenAttachmentIds(newUnseen);
      setPaginationModelCustom({
        pageSize: res?.payload?.data?.data?.pagination?.pageSize,
        page: res?.payload?.data?.data?.pagination?.currentPage,
        nbPage: res?.payload?.data?.data?.pagination?.nbPage,
        idTicket: props.idTicket,
      });
      dispatch(
        setPaginationModelSAVTA({
          page: res?.data?.pagination?.currentPage,
          pageSize: res?.data?.pagination?.pageSize,
          nbPage: res?.data?.pagination?.nbPage,
          idTicket: props.idTicket,
        })
      );
      setIsLoading(false);
      Persistor.persist();
    });
  }, [props.nbreAttachementsNonLus]);

  /* ------------------ Actions ------------------ */
  const handleDownload = (obj) => {
    dispatch(getAttachementById(obj.id)).then((res) => {
      const a = document.createElement("a");
      a.href =
        "data:image/pdf;base64," + res?.payload?.data?.data?.result.contenu;
      a.download = res?.payload?.data?.data?.result?.nom;
      a.click();
    });
  };

  const handleDeleteModal = (obj) => {
    setOpenDeleteModal(true);
    setItemToDelete(obj.id);
  };

  const handleRowClick = (value) => {
    setRowSelectionModel(value[0]);
  };

  useEffect(() => {
    if (rowSelectionModel == !null && rowSelectionModel == !undefined) {
      setAttachmentToPreview(rowSelectionModel);
    }
  }, [rowSelectionModel]);

  /* ------------------ Render ------------------ */
  const totalFiles = props.attachmentList?.length || 0;
  const hasAttachments = totalFiles > 0;

  return (
    <div className={styles.container}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <span className={styles.toolbarTitle}>Pièces jointes</span>
          {totalFiles > 0 && (
            <span className={styles.countBadge}>{totalFiles}</span>
          )}
          {props.nbreAttachementsNonLus > 0 && (
            <span className={styles.toolbarSubtitle}>
              · {props.nbreAttachementsNonLus} non lu
              {props.nbreAttachementsNonLus > 1 ? "s" : ""}
            </span>
          )}
        </div>
        <UploadFile
          ticket={props.idTicket}
          fetchData={fetchData}
          paginationModelCustom={paginationModelCustom}
        />
      </div>

      <DeleteConfirmationDialog
        open={openDeleteModal}
        setOpenDeleteModal={setOpenDeleteModal}
        itemToDelete={itemToDelete}
        setIsLoading={setIsLoading}
        paginationModelCustom={paginationModelCustom}
        setPaginationModelCustom={setPaginationModelCustom}
        setAttachmentList={props.setAttachmentList}
        attachmentList={props.attachmentList}
      />

      {/* Content area: loader → empty state → data grid */}
      <div className={styles.gridWrapper}>
        {isLoading ? (
          <LoadingState />
        ) : !hasAttachments ? (
          <EmptyState />
        ) : (
          <DataGrid
            rows={props.attachmentList}
            columns={columns}
            page={paginationModelCustom?.page}
            setPage={setPage}
            fetchData={fetchData}
            disableColumnSelector
            pageSize={paginationModelCustom?.pageSize}
            loading={isLoading}
            onRowSelectionModelChange={handleRowClick}
            rowSelectionModel={rowSelectionModel}
            getRowId={(row) => row.id}
            rowHeight={56}
            editMode={false}
            getRowClassName={(params) => {
              const isUnseen = unseenAttachmentIds.includes(params.id);
              return isUnseen ? "even" : "odd";
            }}
            slots={{
              pagination: false,
              footer: CustomFooterComponent,
            }}
            slotProps={{
              footer: { paginationModelCustom, fetchData },
            }}
            components={{
              Toolbar: GridToolbar,
            }}
            sx={{
              border: "none",
              "& .MuiDataGrid-virtualScroller": {
                backgroundColor: "#ffffff",
              },
            }}
          />
        )}
      </div>

      {/* Previews */}
      {attachment && (
        <>
          <PreviewPDFComponent
            id={attachment}
            openPreview={openPreview}
            setOpenPreview={setOpenPreview}
          />
          <PreviewImageComponent
            id={attachment}
            openPreviewImage={openPreviewImage}
            setOpenPreviewImage={setOpenPreviewImage}
          />
          <AudioPlayerComponent
            id={attachment}
            openPreviewAudio={openPreviewAudio}
            setOpenPreviewAudio={setOpenPreviewAudio}
          />
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Preview Dialogs                                                    */
/* ------------------------------------------------------------------ */

const DialogHeader = ({ title, onClose }) => (
  <div className={styles.previewDialogHeader}>
    <span className={styles.previewDialogTitle}>{title}</span>
    <IconButton
      className={styles.previewDialogClose}
      onClick={onClose}
      size="small"
    >
      <CloseIcon fontSize="small" />
    </IconButton>
  </div>
);

const PreviewImageComponent = ({ id, openPreviewImage, setOpenPreviewImage }) => {
  const [img, setImg] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();

  const handleClose = () => setOpenPreviewImage(false);

  useEffect(() => {
    setIsLoading(true);
    dispatch(getAttachementById(id))
      .then(unwrapResult)
      .then((res) => setImg(res?.data?.data?.result?.contenu))
      .finally(() => setIsLoading(false));
  }, [id]);

  return (
    <Dialog
      open={openPreviewImage}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      className={styles.previewDialog}
    >
      <DialogHeader title="Aperçu de l'image" onClose={handleClose} />
      <DialogContent sx={{ p: 0 }}>
        <div className={styles.previewBody}>
          {isLoading ? (
            <LoaderComponent label="Chargement de l'image..." />
          ) : img ? (
            <img
              className={styles.previewImage}
              src={`data:image/jpeg;base64,${img}`}
              alt="Aperçu"
            />
          ) : (
            <p>Un problème est survenu lors du téléchargement.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const PreviewPDFComponent = ({ id, openPreview, setOpenPreview }) => {
  const [pdf, setPDF] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();

  const handleClose = () => setOpenPreview(false);

  useEffect(() => {
    setIsLoading(true);
    dispatch(getAttachementById(id))
      .then((res) => setPDF(res?.payload?.data?.data?.result?.contenu))
      .finally(() => setIsLoading(false));
  }, [id]);

  return (
    <Dialog
      open={openPreview}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      className={styles.previewDialog}
      PaperProps={{ style: { minHeight: "85vh" } }}
    >
      <DialogHeader title="Aperçu du document" onClose={handleClose} />
      <DialogContent sx={{ p: 0 }}>
        <div className={styles.previewBody} style={{ padding: 0 }}>
          {isLoading ? (
            <LoaderComponent label="Chargement du PDF..." />
          ) : pdf ? (
            <object
              data={`data:application/pdf;base64,${pdf}`}
              type="application/pdf"
              width="100%"
              height="700px"
              style={{ border: "none" }}
            />
          ) : (
            <p>Erreur lors du chargement du PDF.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AudioPlayerComponent = ({ id, openPreviewAudio, setOpenPreviewAudio }) => {
  const [audio, setAudio] = useState(null);
  const [type, setType] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();

  const handleClose = () => setOpenPreviewAudio(false);

  useEffect(() => {
    setIsLoading(true);
    dispatch(getAttachementById(id))
      .then(unwrapResult)
      .then((res) => {
        setAudio(res?.data?.data?.result?.contenu);
        setType(res?.data?.data?.result?.type);
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  return (
    <Dialog
      open={openPreviewAudio}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className={styles.previewDialog}
    >
      <DialogHeader title="Lecteur audio" onClose={handleClose} />
      <DialogContent sx={{ p: 0 }}>
        <div className={styles.audioWrapper}>
          {isLoading ? (
            <LoaderComponent label="Chargement de l'audio..." />
          ) : audio ? (
            <audio controls>
              <source src={`data:audio/ogg;base64,${audio}`} type={type} />
              Votre navigateur ne supporte pas l'élément audio.
            </audio>
          ) : (
            <p>Un problème est survenu lors du téléchargement de l'audio.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const LoaderComponent = ({ label = "Chargement..." }) => (
  <div className={styles.loaderWrap}>
    <Spin
      indicator={
        <LoadingOutlined style={{ fontSize: 30, color: "#2A66C6" }} spin />
      }
    />
    <span className={styles.loaderText}>{label}</span>
  </div>
);